import type { Translation } from '@/types/translation';

// French (FR) translation file.

const frTranslation: Translation = {
  common: {
    save: 'Enregistrer',
    close: 'Fermer',
    search: 'Rechercher',
    cancel: 'Annuler',
    back: 'Retour',
    continue: 'Continuer',
    submit: 'Envoyer',
    confirm: 'Confirmer',
    delete: 'Supprimer',
    save_changes: 'Enregistrer',
    loading: 'Chargement...',
  },
  pages: {
    account: {
      tabs: {
        dashboard: 'Tableau de bord',
        fleet_overview: "Vue d'ensemble de la flotte",
        unit_overview: 'Toutes les unités',
        campaigns: 'Campagnes',
        members: 'Membres',
      },
      dashboard: {
        active_fault_codes: {
          title: "Codes d'erreur actifs",
          units_title: "Unités avec des codes d'erreur actifs",
        },
        connectivity: {
          title: 'Connectivité',
          units_title: 'Statut de la connectivité',
        },
        fuel_consumption: {
          title: 'Consommation de carburant',
          details_title: 'Détails de la consommation de carburant',
        },
      },
      fleet_overview: {
        pinned_fleets: 'Flottes épinglées',
        other_fleets: 'Autres flottes',
        pin: 'Épingler la flotte',
        unpin: 'Détacher la flotte',
        edit: 'Éditer la flotte',
        remove: 'Supprimer la flotte',
      },
      unit_overview: {
        title: 'Unités',
        unverified_ownership: 'Propriété non vérifiée',
        add_to_fleet: 'Ajouter à la flotte',
      },
      campaigns: {
        active_table: {
          title: 'Campagnes actives',
          download_campaigns: 'Télécharger les campagnes (xlsx)',
          empty_state: {
            title: "Vous n'avez aucune campagne active pour cette unité",
          },
        },
        performed_table: {
          title: 'Campagnes effectuées',
          empty_state: {
            title: 'Aucune campagne effectuée disponible',
          },
        },
      },
      members: {
        title: 'Membres',
        remove_members_from_account: 'Retirer {{selectedRows}} membre(s) du compte',
      },
    },
    fleet: {
      tabs: {
        dashboard: 'Tableau de bord',
        unit_overview: 'Toutes les unités',
        campaigns: 'Campagnes',
      },
      campaigns: {
        title: 'Campagnes actives',
        download_campaigns: 'Télécharger les campagnes (xlsx)',
        empty_state: {
          title: "Vous n'avez aucune campagne active pour cette flotte",
        },
      },
    },
    unit: {
      tabs: {
        specifications: 'Spécifications',
        campaigns: 'Campagnes',
        coverage: 'Garantie et couverture',
        maintenance_history: 'Historique de maintenance',
        manuals_handbooks: 'Manuels et guides',
        diagnostics_log: 'Journal de diagnostic',
        services: 'Services numériques',
        settings: 'Paramètres',
      },
      spare_parts: 'Pièces de rechange',
      manuals_selection_description: 'Pour quel produit souhaitez-vous trouver des manuels et guides.',
      specifications: {
        title: 'Spécifications',
      },
      campaigns: {
        active_table: {
          title: 'Campagnes actives',
          empty_state: {
            title: "Vous n'avez aucune campagne active pour cette unité",
          },
        },
        performed_table: {
          title: 'Campagnes effectuées',
          empty_state: {
            title: 'Aucune campagne effectuée disponible',
          },
        },
      },
      coverage: {
        cta: {
          heading: 'Informations sur la garantie et la couverture',
          message:
            'Pour toute question concernant la garantie et la couverture, contactez votre concessionnaire Volvo Penta ou OEM.',
          find_dealer: 'Trouver le concessionnaire le plus proche',
          learn_more: 'En savoir plus',
        },
        empty_state: {
          title: "Aucune garantie et/ou couverture n'a été trouvée pour cette unité",
        },
      },
      publications: {
        select_menu: {
          category: 'Catégorie',
          designation: 'Désignation',
          product_type: 'Type de produit',
          language: 'Langue',
        },
        empty_state: {
          title: 'Aucune publication disponible pour cette unité',
        },
      },
      services: {
        title: 'Services disponibles',
        all_services: {
          title: 'Activer tous les services',
        },
        list: {
          ActiveMonitoring: {
            Marine: {
              title: 'Extended',
              description:
                'Bénéficiez de tous les avantages de Core, plus des alertes automatiques par e-mail envoyées directement à votre concessionnaire sélectionné.',
              checklist: [
                "Alertez instantanément votre concessionnaire si votre système détecte un code d'erreur critique",
                'Permettez à votre concessionnaire de préparer des actions correctives immédiates',
              ],
            },
            Industrial: {
              title: 'Surveillance active',
              description:
                "Active Monitoring aide les clients à maintenir leurs produits Volvo Penta en fonctionnement en détectant les problèmes tôt, en facilitant un service plus rapide et en offrant une vision claire de l'état et de l'utilisation du produit.\n\nActive Monitoring active également Remote Software Updates, un service permettant d'installer des campagnes logicielles over-the-air, à votre convenance.",
              checklist: [
                'Disponibilité accrue grâce aux alertes précoces et au support proactif',
                "Service plus rapide et mieux préparé grâce aux bonnes informations disponibles à l'avance",
              ],
            },
          },
          ActiveMonitoringCore: {
            Marine: {
              title: 'Core',
              description:
                'Partagez les données de base du véhicule avec votre concessionnaire pour obtenir une assistance plus rapide lors de la maintenance ou en cas de problèmes imprévus.',
              checklist: [
                "Laissez votre concessionnaire surveiller l'état du produit et suivre les heures de fonctionnement",
                "Permettez à votre concessionnaire d'analyser instantanément les codes d'erreur pour une planification proactive de l'entretien",
              ],
            },
            Industrial: {
              title: 'Connected Services Core',
              description:
                'Core permet au concessionnaire Volvo Penta de surveiller le produit Volvo Penta. <ActiveMonitoringCore>Pour plus de détails, consultez la description du service pour Connected Services Core.</ActiveMonitoringCore>',
              checklist: [],
            },
          },
          ActiveMonitoringEcWifi: {
            Marine: {
              title: 'Extended',
              description:
                "Permettez à votre concessionnaire de suivre les données du véhicule et de recevoir des alertes instantanées en cas de codes d'erreur critiques afin de préparer des actions correctives immédiates.",
              checklist: [
                "Alertez instantanément votre concessionnaire si votre système détecte un code d'erreur critique",
                'Permettez à votre concessionnaire de préparer des actions correctives immédiates',
              ],
            },
            Industrial: {
              title: 'Extended',
              description:
                "Permettez à votre concessionnaire de suivre les données du véhicule et de recevoir des alertes instantanées en cas de codes d'erreur critiques afin de préparer des actions correctives immédiates.",
              checklist: [
                "Alertez instantanément votre concessionnaire si votre système détecte un code d'erreur critique",
                'Permettez à votre concessionnaire de préparer des actions correctives immédiates',
              ],
            },
          },
          Penta_Position: {
            Marine: {
              title: 'Services de localisation',
              description:
                'Location Services aide les clients à localiser et gérer rapidement leurs produits Volvo Penta connectés grâce à une vue cartographique dans Volvo Penta Connect, facilitant la gestion du service et la supervision de la flotte.',
              checklist: [
                'Meilleure visibilité de la flotte grâce à une carte affichant la dernière position des unités connectées',
                "Réponse de service plus rapide grâce à un accès facile à la position de l'unité, y compris les heures de fonctionnement et les codes d'erreur",
              ],
            },
            Industrial: {
              title: 'Services de localisation',
              description:
                'Location Services aide les clients à localiser et gérer rapidement leurs produits Volvo Penta connectés grâce à une vue cartographique dans Volvo Penta Connect, facilitant la gestion du service et la supervision de la flotte.',
              checklist: [
                'Meilleure visibilité de la flotte grâce à une carte affichant la dernière position des unités connectées',
                "Réponse de service plus rapide grâce à un accès facile à la position de l'unité, y compris les heures de fonctionnement et les codes d'erreur",
              ],
            },
          },
        },
        request_sent: 'Demande envoyée',
        empty_state: {
          title: 'Provisionnement de service non disponible',
          message: 'Le provisionnement des services numériques n’est pas disponible pour cette unité.',
        },
        active_services_title: 'Services actifs',
        available_services_title: 'Services disponibles',
        activate_button: 'Activer le service',
        terminate_button: 'Résilier',
        read_more_button: 'En savoir plus',
        requires_prefix: 'Nécessite {{serviceName}}',
      },
      settings: {
        unit_settings: {
          title: "Paramètres de l'unité",
          unit_name: "Nom de l'unité",
          home_port: "Port d'attache",
          operating_location: "Lieu d'exploitation",
          upload_image: 'Importer une image',
          add_files: 'Ajouter des fichiers',
          helperText: 'Fichier .png ou .jpg de 3 Mo max',
        },
        network_settings: {
          title: 'Paramètres réseau',
          empty_state: {
            title: 'Aucun réseau actif disponible',
            message: "Les paramètres réseau pour cette unité apparaîtront ici une fois qu'ils auront été configurés.",
          },
          updating_title: 'Mise à jour des paramètres réseau',
          updating_description: 'Veuillez patienter pendant que nous récupérons la dernière configuration réseau.',
        },
      },
      maintenance: {
        service_protocol_for_chassi: 'Protocole de service pour châssis {{chassisNumber}}',
        download_pdf: 'Télécharger le PDF',
        service: 'Service {{type}}',
        default_service: 'Chaîne cinématique {{chassisSeries}} {{chassisNumber}}',
        general_comment: 'Commentaire général sur le service',
        no_comment: 'Aucun commentaire général pour ce protocole de service.',
        periodic_tag: 'Tous les {{months}} mois / {{engineHours}} h',
        non_periodic_month_tag: 'À {{months}} mois',
        non_periodic_hours_tag: 'À {{engineHours}} h',
        empty_state: {
          title: 'Aucun historique de maintenance disponible',
          message:
            "Les protocoles de service pour ce moteur apparaîtront ici une fois qu'ils auront été enregistrés par un concessionnaire Volvo Penta.",
        },
        unverified_ownership_notice:
          "Certaines informations peuvent être limitées car la propriété de cette unité n'a pas encore été vérifiée. Contactez votre concessionnaire Volvo Penta agréé pour finaliser la vérification.",
      },
    },
    settings: {
      application_settings: "Paramètres de l'application",
      account_name: 'Nom du compte flotte',
      notifications: 'Notifications',
      email: 'E-mail',
      email_notifications: 'Activer les notifications par e-mail',
      sms: 'SMS',
      sms_notifications: 'Activer les notifications par SMS',
    },
    support: {
      title: 'Questions fréquemment posées',
      description: 'Trouvez des informations de contact ainsi que des informations générales sur Volvo Penta Connect.',
      professional_support: {
        tag: 'Support professionnel',
        title: 'Les experts ultimes - à votre service',
        description:
          "Contactez un concessionnaire Volvo Penta agréé en tant que premier point de contact lorsque vous avez besoin d'aide pour le service, la réparation, les pièces ou tout autre support - pour les navires de plaisance et commerciaux.",
      },
      emergency_support: {
        tag: "Support d'urgence",
        title: 'Volvo Penta Action Service',
        description:
          "Une panne moteur est rare, mais si cela se produit, Action Service est là pour vous mettre en contact avec le partenaire Volvo Penta le plus proche. Nous offrons un support dans plusieurs langues, 24h/24 et 7j/7, toute l'année.",
      },
      platform_support: {
        tag: 'Support de la plateforme',
        title: 'Support Volvo Penta Connect',
        description:
          "Contactez notre équipe dédiée pour obtenir de l'aide avec votre compte et les fonctionnalités de Volvo Penta Connect. Nous sommes là pour vous aider à gérer vos données moteur et votre connectivité en toute simplicité.",
      },
      getting_started: {
        title: 'Premiers pas',
        question_1: {
          title: 'Pour qui est Volvo Penta Connect ?',
          body: "Volvo Penta Connect est un portail client qui vous donne un aperçu de votre ou vos unités et un accès à des informations pertinentes telles que les manuels et guides, les campagnes, l'état de la garantie, et plus encore.",
        },
        question_2: {
          title: 'Que dois-je faire en premier pour utiliser ce produit ?',
          body: "Vous devez enregistrer une unité sur votre compte. Une fois qu'une unité est ajoutée, vous pouvez surveiller les données de l'unité, installer des mises à jour logicielles over-the-air et accéder aux manuels et guides liés à cette unité.",
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Ajouter une unité' },
        },
        question_3: {
          title: "Puis-je utiliser ce produit sans ajouter d'unité ?",
          body: "Non. Volvo Penta Connect est construit autour de vos unités. Les manuels, les données moteur et les services numériques sont tous liés à une unité spécifique. Si vous ne possédez pas ou ne prévoyez pas d'enregistrer une unité, ce produit ne répondra pas à vos besoins.",
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Ajouter une unité' },
        },
        question_4: {
          title: 'Comment enregistrer mon moteur ?',
          body: "Allez à la liste des unités et sélectionnez Ajouter une unité. Recherchez le numéro de série, puis complétez et soumettez le formulaire d'enregistrement. Certains moteurs industriels plus récents sont livrés avec un code QR préinstallé sur le moteur. Si disponible, vous pouvez scanner ce code pour commencer le processus d'enregistrement.",
        },
        question_5: {
          title: 'Quelles informations dois-je fournir ?',
          body: "Commencez par enregistrer votre unité dans Volvo Penta Connect. Allez à la liste des unités et sélectionnez Ajouter une unité, puis recherchez le numéro de série de l'unité et complétez le formulaire d'enregistrement.<br /><br />Une fois l'unité enregistrée, vous aurez accès à des informations telles que les manuels, les pièces de rechange, les campagnes, l'état de la garantie et les services numériques liés à cette unité.",
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Ajouter une unité' },
        },
        question_6: {
          title: 'Que puis-je faire une fois mon unité enregistrée ?',
          body: "Avec une unité enregistrée, vous pouvez :<br /><br /><ul><li>- Regrouper les unités en flottes par type de véhicule ou cas d'utilisation</li><li>- Surveiller l'état de l'unité</li><li>- Installer des mises à jour logicielles over-the-air</li><li>- Accéder aux manuels et guides spécifiques à votre unité</li></ul>",
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Aperçu des unités' },
        },
        question_7: {
          title: 'Où puis-je trouver les manuels et guides ?',
          body: "Les manuels et guides deviennent disponibles une fois qu'une unité est ajoutée à votre compte, afin que nous puissions afficher la documentation correspondant à votre unité spécifique.",
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Ajouter une unité' },
        },
        question_8: {
          title: 'Comment ajouter un utilisateur à mon compte de flotte ?',
          body: "Pour ajouter un nouvel utilisateur, vous devez l'inviter depuis la page Membres.<br /><br /><ul><li>1. Allez à Membres</li><li>2. Sélectionnez Ajouter un membre</li><li>3. Créez un lien d'invitation et copiez-le</li><li>4. Partagez le lien avec la personne que vous souhaitez inviter</li><li>5. Une fois l'invitation acceptée, attribuez un rôle au nouveau membre</li></ul><br/>Seuls les utilisateurs ayant la permission de gérer les membres peuvent envoyer des invitations et attribuer des rôles.",
          link: { href: 'https://www.volvopenta.com/connect/members', label: 'Membres' },
        },
        question_9: {
          title: "Pourquoi mon unité est-elle marquée comme 'Propriétaire non vérifié' ?",
          body: "Les unités nouvellement enregistrées sont temporairement marquées comme Propriétaire non vérifié jusqu'à ce que la propriété soit confirmée.<br /><br />La propriété peut être vérifiée par un concessionnaire Volvo Penta autorisé ou par notre équipe de support. Une fois vérifiée, l'étiquette sera automatiquement supprimée.<br /><br />Si vous avez besoin d'aide pour la vérification, veuillez contacter votre concessionnaire Volvo Penta autorisé.",
        },
        question_10: {
          title: 'Pourquoi ne puis-je pas voir mes unités ?',
          body: "Vos unités peuvent ne pas apparaître dans votre compte pour plusieurs raisons. Veuillez vérifier ces scénarios courants :<br /><br />Incohérence d'email :<br/>Vos unités sont liées à une adresse email spécifique. Si vous avez créé un compte avec une adresse différente, les unités ne seront pas visibles. Vous pouvez créer un nouveau compte avec l'email correct ou contacter un concessionnaire Volvo Penta autorisé pour vous aider à réenregistrer votre unité.<br /><br />Enregistrement en attente :<br/>Si vous avez ajouté une nouvelle unité industrielle, elle peut ne pas encore être dans nos dossiers. Ces unités n'apparaîtront pas dans Volvo Penta Connect tant que notre équipe de support n'aura pas terminé le processus d'enregistrement manuel.<br /><br />Rôles du compte :<br/>Votre accès à des unités spécifiques dépend de votre rôle attribué. Si vous pensez que votre accès est restreint, veuillez contacter votre gestionnaire de flotte pour vous assurer que vous avez le rôle de compte correct.",
        },
      },
      connectivity_telematics: {
        title: 'Connectivité et télématique',
        question_1: {
          title: 'Comment savoir si mon unité est connectée ?',
          body: 'Vous pouvez voir l’état de connectivité d’une unité à côté de son nom dans la liste des unités et sur la page de l’unité.<br /><br /><GreenDot /> – L’unité est en ligne. Des données ont été reçues au cours des 2 dernières heures.<br /><br /><GreyDot /> – L’unité est hors ligne. Aucune donnée n’a été reçue au cours des 2 dernières heures.<br /><br /><YellowDot /> – La connectivité est disponible mais pas entièrement configurée.<br /><br />Pas de point – L’unité n’a pas de capacité de connectivité. Elle peut être éligible à l’installation d’une passerelle télématique (TGW) via un concessionnaire.<br /><br />Si votre unité est hors ligne, elle peut toujours être utilisée normalement, mais les mises à jour des données reprendront une fois l’unité reconnectée.',
        },
        question_2: {
          title: 'Pourquoi mon unité n’affiche-t-elle aucun état de connectivité ?',
          body: 'Les unités sans indicateur d’état n’ont pas de passerelle télématique (TGW) installée ou activée. Ces unités peuvent être connectées en installant une TGW via un concessionnaire Volvo Penta.',
        },
        question_3: {
          title: 'Qu’est-ce qu’une passerelle télématique (TGW) ?',
          body: 'Une passerelle télématique (TGW) est un dispositif matériel installé sur l’unité qui permet la connectivité et le transfert de données vers Volvo Penta Connect.<br /><br />Sans TGW, l’unité ne peut pas être surveillée numériquement.',
        },
        question_4: {
          title: 'Comment connecter mon unité à Volvo Penta Connect ?',
          body: 'Pour connecter une unité, les étapes suivantes doivent être complétées :<br /><br /><ul><li>- L’unité doit avoir une TGW installée</li><li>- La TGW doit être activée par un concessionnaire ou un OEM</li><li>- Les services numériques requis doivent être activés</li><li>- Le propriétaire doit approuver l’accord de gestion des données</li></ul><br /><br />Une fois ces étapes terminées, l’unité apparaîtra avec l’état de connectivité dans Volvo Penta Connect.',
        },
        question_5: {
          title: 'Quels smartphones/tablettes sont compatibles avec l’application Volvo Penta Connect ?',
          body: 'Pour les appareils iOS, la version du système d’exploitation doit être iOS 17 ou ultérieure. Pour les appareils Android, la version du système d’exploitation doit être 9.0 ou ultérieure.',
        },
        question_6: {
          title: 'Qui peut m’aider à installer ou activer la connectivité ?',
          body: 'Un concessionnaire Volvo Penta peut aider à l’installation, à l’activation et à la configuration des services de la TGW.',
          link: {
            href: 'https://www.volvopenta.com/about-us/dealer-locator/',
            label: 'Trouver un concessionnaire Volvo Penta',
          },
        },
        question_7: {
          title: 'Comment configurer le Wi-Fi pour mon unité ?',
          body: "Vous pouvez configurer votre connexion Wi-Fi directement dans l'application. Suivez ces étapes pour commencer :<br /><br /><ul><li>- **Accéder aux paramètres :** Rendez-vous dans la section de configuration Wi-Fi de votre tableau de bord.</li><li>- **Suivre le guide :** Utilisez le manuel d'instructions (PDF) pour des instructions étape par étape afin de compléter votre configuration.</li><li>- **Connecter votre unité :** Une fois configurée, votre unité restera automatiquement connectée à votre espace de travail numérique.</li></ul>",
          link: {
            href: 'https://www.volvopenta.com/connect/documents/Connect-to-WiFi-onboard.pdf',
            label: "Manuel d'instructions",
            icon: 'import',
          },
        },
      },
      campaigns_software_updates: {
        title: 'Campagnes et mises à jour logicielles',
        question_1: {
          title: 'Qu’est-ce qu’une campagne ?',
          body: 'Une campagne est une action initiée par Volvo Penta pour garantir la qualité du produit. Si une campagne concerne l’une de vos unités enregistrées, vous serez informé et guidé à travers les étapes requises.',
          link: {
            href: 'https://www.volvopenta.com/connect/campaigns',
            label: 'Aperçu des campagnes',
          },
        },
        question_2: {
          title: 'Que dois-je faire lorsque j’ai une campagne active ?',
          body: 'Cela dépend du type de campagne :<br /><br /><ul><li>- **Rappel / Qualité / Service :** Contactez votre concessionnaire Volvo Penta local</li><li>- **Installation / OTA :** Ces campagnes peuvent être effectuées à distance via l’application Volvo Penta. Téléchargez l’application et suivez les instructions intégrées.</li></ul>',
          link: {
            href: 'https://www.volvopenta.com/about-us/dealer-locator/',
            label: 'Trouver votre concessionnaire',
          },
          ios: { href: '#', label: 'App store' },
          android: { href: '#', label: 'Google play' },
        },
        question_3: {
          title: 'Que dois-je préparer avant d’installer une campagne over-the-air (OTA) ?',
          body: 'Avant de commencer une installation OTA, assurez-vous que :<br /><br /><ul><li>- Vous avez sélectionné l’unité correcte</li><li>- Le Bluetooth est activé sur votre appareil</li><li>- Vous êtes à moins de 3 mètres de l’unité</li><li>- Toutes les conditions préalables requises indiquées dans le produit sont remplies</li><li>- L’unité reste alimentée et non perturbée pendant l’installation</li></ul><br/>Si une exigence n’est pas remplie, l’installation ne démarrera pas.',
        },
        question_4: {
          title: 'J’ai des problèmes pour installer une campagne over-the-air (OTA).',
          body: 'Si l’installation échoue ou ne démarre pas :<br /><br /><ul><li>- Vérifiez que le Bluetooth est activé et que vous êtes proche de l’unité</li><li>- Restez à moins de 3 mètres pendant toute l’installation</li><li>- Redémarrez l’application et réessayez</li><li>- Vérifiez à nouveau que toutes les conditions préalables de l’unité sont remplies</li></ul><br/>Si le problème persiste, contactez le support.',
        },
        question_5: {
          title: 'Où puis-je voir mes installations over-the-air (OTA) terminées ?',
          body: 'Vous pouvez consulter les installations OTA terminées sur la page de l’unité. Ouvrez l’unité et allez dans l’onglet Campagnes pour voir l’historique complet des installations.',
        },
        question_6: {
          title: 'Une mauvaise connectivité peut-elle affecter une installation OTA ?',
          body: 'Oui. Une installation OTA peut échouer si l’unité a une connectivité cellulaire ou Wi-Fi instable ou inexistante. Assurez-vous que l’unité dispose d’une connexion fiable et reste connectée jusqu’à la fin de l’installation.',
        },
        question_7: {
          title: 'Pourquoi mon unité doit-elle être régulièrement connectée ?',
          body: 'Une connectivité régulière permet de s’assurer que votre unité est prête pour les mises à jour logicielles. Si une unité n’a pas été connectée pendant un certain temps, des données ou préparations requises peuvent manquer, ce qui peut retarder ou bloquer une installation OTA.',
        },
        question_8: {
          title: 'Que se passe-t-il si je ne termine pas une campagne à temps ?',
          body: 'Les campagnes ne sont pas installées automatiquement. Si une campagne n’est pas terminée dans le délai spécifié, il peut ne plus être possible de la réaliser. Suivez toujours les instructions affichées et terminez la campagne dans le délai indiqué.',
        },
      },
      maintenance_service: {
        title: 'Maintenance & service',
        question_1: {
          title: "Puis-je voir l'historique de maintenance de mon unité ?",
          body: 'Oui. Chaque unité dispose d’un onglet Historique de maintenance où les services effectués sont répertoriés. Seuls les services réalisés par des concessionnaires Volvo Penta agréés sont affichés. Si un service est manquant, veuillez contacter directement votre concessionnaire.<br /><br />Sélectionnez une unité et ouvrez l’onglet Historique de maintenance pour voir les détails.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Aperçu des unités' },
        },
        question_2: {
          title: 'Pourquoi ne puis-je pas voir quel concessionnaire a effectué un service ?',
          body: 'Les détails du concessionnaire ne sont affichés qu’aux propriétaires vérifiés de l’unité.<br /><br />Pour voir le nom et l’emplacement du concessionnaire pour un service terminé, vous devez être enregistré en tant que propriétaire. Si vous n’êtes pas encore vérifié, veuillez contacter votre concessionnaire Volvo Penta agréé pour confirmer votre propriété.',
        },
      },
      warranty_coverage: {
        title: 'Garantie et couverture étendue',
        question_1: {
          title: 'Comment savoir si mon unité dispose d’une garantie et d’une couverture ?',
          body: 'Chaque unité dispose d’un onglet Garantie et couverture où sont affichées les garanties actives. Sélectionnez une unité et ouvrez l’onglet pour voir les détails.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Aperçu des unités' },
        },
        question_2: {
          title: 'Qu’est-ce que la garantie standard ?',
          body: 'Tous les produits Volvo Penta incluent une garantie standard d’usine. La couverture s’applique pendant une période définie ou un nombre d’heures de fonctionnement—selon la première éventualité—et dépend du produit et de son application.',
        },
        question_3: {
          title: 'Qu’est-ce que la garantie standard sans limite d’heures ?',
          body: 'Certaines installations n’ont pas de limite d’heures de fonctionnement pendant la première et/ou la deuxième année de couverture de la garantie. Les détails dépendent du produit et de l’installation et sont définis dans les conditions de garantie.',
        },
        question_4: {
          title: 'Qu’est-ce que la garantie des composants ?',
          body: 'La garantie des composants couvre des composants majeurs spécifiques de votre produit Volvo Penta. La durée de couverture et les limites d’heures de fonctionnement varient selon le composant et sont définies dans les conditions de garantie.',
        },
        question_5: {
          title: 'Qu’est-ce que la garantie des émissions ?',
          body: 'Les moteurs approuvés selon les réglementations US EPA ou California ARB incluent une couverture de garantie des émissions. Cela s’applique pendant une période définie ou un nombre d’heures de fonctionnement, comme indiqué dans les conditions de garantie des émissions.',
        },
      },
      notification: {
        title: 'Notifications',
        question_1: {
          title: 'Pourquoi ne reçois-je pas de notifications concernant mon ou mes unités ?',
          body: 'Vérifiez vos paramètres de notification. Assurez-vous que les notifications sont activées sur la page des paramètres.',
          link: { href: 'https://www.volvopenta.com/connect/settings', label: 'Paramètres' },
        },
      },
    },
    login: {
      hero_title: 'Bienvenue dans Volvo Penta Connect',
      hero_body:
        'Tout ce qui concerne votre moteur ou produit Volvo Penta en un seul endroit. Manuels, guides, pièces de rechange, rappels, campagnes qualité, état de la garantie, et plus encore.',
      log_in: 'Se connecter',
      blurb_back_button: 'Accueil',
      blurb_bolded_main_text:
        'Bienvenue dans Volvo Penta Connect, la nouvelle plateforme pour les gestionnaires de flotte et les propriétaires de bateaux de plaisance.',
      blurb_regular_main_text:
        "Enregistrez votre moteur pour accéder à des informations pertinentes telles que les manuels, les rappels et l'état de la garantie. L'enregistrement garantit que votre concessionnaire local est prêt à vous soutenir avec les bonnes pièces et services. Et le meilleur, c'est que c'est entièrement gratuit.",
      CTAs: {
        manuals_label: 'Accès pratique aux manuels',
        manuals_title: 'Tous vos manuels en un clic',
        manuals_body:
          "Avec Volvo Penta Connect, vous n'avez plus besoin de rechercher le numéro de série pour accéder aux manuels. Notre plateforme offre une solution en un clic, vous donnant accès à l'ensemble des manuels et guides pour tous vos moteurs et produits.",
        efficiency_label: 'Efficacité',
        efficiency_title: 'Trouvez rapidement les bonnes pièces de rechange',
        efficiency_body:
          "Volvo Penta Connect propose une fonctionnalité simple d'utilisation qui facilite la recherche de la bonne pièce de rechange pour votre moteur Volvo Penta, sans avoir à chercher le numéro de série ou le modèle du moteur.",
        service_label: 'Suivi de la maintenance des produits',
        service_title: "Consultez l'historique des services enregistrés pour vos produits",
        service_body:
          "Restez informé de l'état de vos produits avec Volvo Penta Connect. Obtenez une vue d'ensemble complète, y compris les détails de service, de votre historique de services enregistrés pour tous vos produits Penta.",
      },
      cards: {
        uptime_label: 'Assurer la disponibilité',
        uptime_title: 'Découvrez si vous avez des campagnes actives',
        uptime_body:
          "Avec Volvo Penta Connect, le suivi des campagnes de vos produits n'a jamais été aussi simple. Assurez-vous que vos produits sont à jour et fonctionnent toujours au mieux.",
        connect_label: 'Suivre toutes vos garanties',
        connect_title: 'Vos garanties dans Volvo Penta Connect',
        connect_body:
          "Obtenez une vue d'ensemble de toutes vos garanties et couvertures. Vérifiez si elles sont actives, leur durée de validité restante et recevez des notifications lorsqu'elles sont sur le point d'expirer.",
      },
    },
    welcome: {
      title: 'Bienvenue !',
      body: 'Pour tirer pleinement parti de Volvo Penta Connect, vous devez enregistrer un bateau ou une machine sur votre compte. Une fois ajouté, vous aurez accès aux manuels et guides, un accès plus facile aux pièces de rechange pertinentes, aux statuts de garantie, et bien plus encore.',
      get_help: "Obtenir de l'aide",
      get_started: 'Commencer',
      accordion: {
        who_is_it_for: {
          title: 'Pour qui est Volvo Penta Connect ?',
          body: "Volvo Penta Connect est un portail client où vous obtenez une vue d'ensemble de vos unités et des informations pertinentes telles que les manuels et guides, les campagnes, l'état de la garantie et bien plus encore.",
        },
        how_to_register: {
          title: 'Puis-je utiliser ce produit sans enregistrer une unité ?',
          body: "Vous devez enregistrer une unité sur votre compte. Une fois une unité ajoutée, vous pouvez surveiller les données de l'unité, installer des mises à jour logicielles over-the-air et accéder aux manuels et guides liés à cette unité.",
        },
        can_i_use_without_registering: {
          title: 'Où puis-je trouver les manuels et guides ?',
          body: "Les manuels et guides deviennent disponibles une fois qu'une unité est ajoutée à votre compte, afin que nous puissions afficher la documentation correspondant à votre unité spécifique.",
        },
      },
    },
  },
  components: {
    modal: {
      accept_invitation_modal: {
        invite_title: 'Bienvenue chez {{name}} !',
        invite_description:
          'Vous avez été invité à l’espace de travail {{fleetName}}. Ici, vous pouvez surveiller la santé de la flotte et des unités, suivre les analyses, ainsi que recevoir des notifications sur les mises à jour des campagnes et des garanties.',
        accept: "Accepter l'invitation",
        dismiss: "Refuser l'invitation",
      },
      add_units_fleet_modal: {
        button_add: 'Créer une flotte',
        button_update: 'Mettre à jour la flotte',
        title_add: 'Créer une flotte',
        title_update: 'Mettre à jour la flotte',
        title_success: 'Flotte créée avec succès',
        text_success:
          'Votre flotte a été créée. Si la nouvelle flotte n’apparaît pas dans la liste, veuillez actualiser la page.',
        fleet_name: 'Entrez le nom de la flotte ou sélectionnez votre flotte',
        select_your_fleet: 'Sélectionnez votre flotte',
        click_to_create_fleet: 'Cliquez pour créer une flotte',
        table: {
          unit_name: "Nom de l'unité",
          location: 'Emplacement',
          engines: 'Moteurs',
          fleets: 'Flottes',
        },
        cancel: "Ignorer pour l'instant",
        submit: {
          add_units: 'Ajouter des unités à la flotte',
          create_fleet: 'Créer une flotte',
          close: 'Fermer',
        },
      },
      remove_units_fleet_modal: {
        button_remove: 'Retirer de la flotte',
        title_remove: 'Retirer des unités de la flotte',
        description:
          'Vous êtes sur le point de retirer les unités sélectionnées de la flotte. Cette action est irréversible.',
        confirmation: 'Êtes-vous sûr de vouloir retirer les unités sélectionnées de la flotte ?',
        cancel: 'Annuler',
        submit: {
          create_fleet: 'Retirer de la flotte',
        },
      },
      delete_fleet_modal: {
        title: 'Supprimer la flotte',
        description:
          "La suppression d'une flotte de votre compte est définitive. Vous aurez la possibilité de réajouter une flotte à tout moment.",
        confirmation: 'Êtes-vous sûr de vouloir supprimer la flotte suivante ?',
        submit: 'Oui, supprimer la flotte du compte',
      },
      edit_fleet_modal: {
        title: 'Modifier la flotte',
        fleet_name: 'Nom de la flotte',
        select_icon: "Sélectionner l'icône de la flotte",
      },
      add_new_unit_modal: {
        button: 'Ajouter une unité',
        search_unit: {
          title: 'Trouvez votre unité',
          description: "Recherchez l'unité que vous souhaitez ajouter en utilisant le numéro de série du moteur.",
          serial_number: 'Numéro de série du moteur',
          find_serial_number: 'Comment trouver votre numéro de série',
          is_not_found: {
            label: "Le numéro de série n'existe pas dans nos archives",
            body: "Veuillez vous assurer d'avoir trouvé le bon numéro de série. Si cela ne fonctionne toujours pas, contactez votre concessionnaire Volvo Penta ou trouvez-en un dans notre <a>Localisateur de concessionnaires</a> pour obtenir de l'aide.",
          },
          is_not_registered_industrial: {
            label: 'Unité non enregistrée pour ce produit',
            body: "Enregistrez l'unité afin de l'ajouter à votre compte. La demande d'enregistrement sera traitée par notre équipe de support et l'unité sera ajoutée une fois la procédure terminée.",
          },
          is_not_registered_marine: {
            label: 'Unité non enregistrée pour ce produit',
            body: "L'unité doit être enregistrée pour que vous puissiez l'ajouter à votre compte. Veuillez contacter votre concessionnaire Volvo Penta ou en trouver un dans notre <a>Localisateur de concessionnaires</a> pour obtenir de l'aide concernant l'enregistrement de l'unité.",
          },
          is_maximum_units_reached: {
            label: "Nombre maximum d'unités atteint",
            body: "Vous pouvez ajouter jusqu'à trois unités non possédées. Pour en ajouter davantage, vous devez être enregistré en tant que propriétaire de l'unité. Veuillez contacter votre concessionnaire Volvo Penta pour obtenir de l'aide, ou utilisez notre Localisateur de concessionnaires pour en trouver un près de chez vous.",
          },
          is_unit_already_added: {
            label: 'Unité déjà ajoutée à votre compte',
            body: "Cette unité est déjà disponible dans votre compte et n'a pas besoin d'être enregistrée à nouveau. Vous pouvez la trouver dans votre liste d'unités.",
          },
        },
        unit_information: {
          title: "Entrez les informations de l'unité",
          description:
            'Veuillez remplir les détails ci-dessous pour ajouter votre navire à Volvo Penta Connect et recevoir des informations pertinentes sur votre produit Penta.',
          privacy_information:
            "Volvo Penta enregistre et utilise certaines données personnelles vous concernant. Si vous souhaitez en savoir plus sur la façon dont Volvo Penta traite vos données personnelles, veuillez consulter <a>Informations sur la confidentialité pour l'enregistrement du propriétaire ou de l'opérateur</a>.",
        },
        marine_unit_information: {
          serial_number: 'Numéro de série du moteur',
          serial_number_helperText: 'Le numéro de série Volvo Penta se trouve sur la plaque moteur.',
          vessel_name: 'Nom du navire',
          vessel_name_helperText: 'Le nom (par ex. Black Pearl) donné à votre navire.',
          vessel_id: 'ID du navire',
          imo_number: 'Numéro IMO',
          imo_number_helperText:
            "Numéro de l'Organisation Maritime Internationale identifiant de manière unique votre navire.",
          mmsi_number: 'Numéro MMSI',
          mmsi_number_helperText: "Numéro d'identité du service mobile maritime utilisé pour les communications radio.",
          manufacturer: 'Fabricant',
          manufacturer_helperText: 'Le fabricant de votre navire.',
          model: 'Modèle',
          model_helperText: 'Le modèle du navire.',
          vessel_segment: 'Segment du navire',
          type_of_vessel: 'Type de navire',
          home_port: "Port d'attache",
          home_port_helperText: "Site ou port d'attache de votre navire",
          add_unit: 'Ajouter une unité',
        },
        marine_unit_added: {
          title: 'Votre unité a été ajoutée',
          description:
            'Votre navire est ajouté et vous pourrez voir les informations de votre navire, télécharger des manuels, trouver des pièces de rechange, et plus encore.',
          add_another_unit: 'Ajouter une autre unité',
          go_to_unit: "Aller à l'unité",
        },
        industrial_contact_information: {
          title: 'Saisir les informations de contact',
          description:
            'Veuillez remplir les détails des informations du propriétaire ci-dessous pour ajouter votre unité. Vos informations saisies seront contrôlées manuellement par notre équipe de support avant de vous enregistrer en tant que propriétaire.',
          proof_of_ownership: 'Preuve de propriété',
          proof_of_ownership_helperText:
            "Une preuve de propriété (par ex. contrat, reçu ou confirmation de commande) est requise pour enregistrer l'entreprise en tant que propriétaire de la machine. Si vous ne pouvez pas fournir de preuve de propriété, veuillez contacter votre concessionnaire.",
          proof_of_ownership_upload:
            'Glissez-déposez ou cliquez pour parcourir les fichiers, fichiers PDF, png, jpg ou heic de 3 Mo maximum chacun.',
          proof_of_ownership_qr: 'Propriété confirmée par code QR',
          contact_person: 'Personne de contact',
          contact_person_description:
            "Vous serez enregistré en tant que personne de contact pour l'entreprise et cette machine.",
          first_name: 'Prénom',
          last_name: 'Nom de famille',
          email: 'Email',
          company_details: "Détails de l'entreprise",
          company_details_description: "Saisissez les informations de l'entreprise propriétaire de la machine.",
          company_name: "Nom de l'entreprise",
          company_name_helperText: "L'entreprise propriétaire de la machine.",
          vat_number: 'Numéro de TVA',
          address_line: 'Adresse',
          zip_code: 'Code postal',
          city: 'Ville',
          state: 'État / Province',
          country: 'Pays',
          notification: 'Préférences de notification',
        },
        industrial_unit_information: {
          engine_serial_number: 'Numéro de série du moteur',
          engine_serial_number_helperText:
            "Si votre machine a plus d'un moteur, cliquez pour ajouter un autre champ et saisir le numéro de série du moteur supplémentaire.",
          add_engine: 'Ajouter un moteur',
          machine_name: 'Nom de la machine',
          machine_name_helperText: 'Le nom (par ex. T4, Unité 11) de votre machine, véhicule ou groupe électrogène.',
          machine_id: 'ID de la machine',
          machine_id_helperText:
            "Le numéro d'identification unique fourni par le fabricant d'équipement d'origine (OEM).",
          manufacturer: 'Fabricant',
          manufacturer_helperText:
            'Le fabricant de votre machine, véhicule ou groupe électrogène (par ex. Magni, Inmesol, Atlas).',
          model: 'Modèle',
          model_helperText: 'Le modèle de votre machine, véhicule ou groupe électrogène.',
          machine_segment: 'Segment de la machine',
          machine_segment_helperText:
            'Le segment dans lequel se trouve votre machine (par ex. Manutention, exploitation minière, foresterie).',
          type_of_machine: 'Type de machine',
          type_of_machine_helperText:
            'Le type de machine (par ex. tracteur de terminal, récolteuse, chariot élévateur).',
          operating_location: "Lieu d'exploitation",
          operating_location_helperText: "Site ou lieu d'exploitation de votre machine.",
        },
        industrial_unit_added: {
          title: 'Unité ajoutée',
          description:
            'Votre machine est ajoutée et vous pourrez voir les informations de votre machine, télécharger des manuels, trouver des pièces de rechange, et plus encore.',
          manual_ownership_registration: 'Enregistrement manuel de propriété',
          manual_ownership_registration_description:
            "L'enregistrement et le contrôle de propriété sont effectués manuellement par notre équipe de support. <b>Actuellement, nous faisons face à une forte demande, ce qui entraîne de longs délais de traitement, comptez entre 2 à 6 semaines.</b> Vous recevrez un e-mail de confirmation lorsque votre demande sera approuvée.",
          add_another_unit: 'Ajouter une autre unité',
          go_to_unit: "Aller à l'unité",
        },
        industrial_no_unit_added: {
          title: "La machine ne s'affichera pas dans Volvo Penta Connect",
          description:
            "Votre machine n'existe pas dans nos archives. Elle ne s'affichera pas dans Volvo Penta Connect tant que le processus d'enregistrement manuel par notre équipe de support ne sera pas terminé.",
          manual_registration: 'Enregistrement manuel',
          manual_registration_description:
            "L'enregistrement et le contrôle de propriété sont effectués manuellement par notre équipe de support. <b>Actuellement, nous faisons face à une forte demande, ce qui entraîne de longs délais de traitement, comptez entre 2 à 6 semaines.</b> Vous recevrez un e-mail de confirmation lorsque votre demande sera approuvée.",
          manuals_campaigns: 'Manuels, pièces et campagnes',
          manuals_campaigns_description:
            "Si vous recherchez des manuels et guides, des pièces de rechange ou des campagnes - voici des liens où vous pouvez les obtenir jusqu'à ce que l'enregistrement soit terminé. Vous avez également reçu ces informations dans votre boîte e-mail.",
          add_another_unit: 'Ajouter une autre unité',
        },
      },
      invite_members_modal: {
        button: 'Ajouter des membres',
        title: "Liens d'invitation en attente",
        empty_state: {
          title: 'Aucune invitation trouvée.',
          message: 'Essayez de créer et de copier une invitation dans votre presse-papiers avec le bouton ci-dessous.',
        },
        generate_invitation_link: "Générer et copier un nouveau lien d'invitation",
      },
      remove_members_modal: {
        title: 'Supprimer le(s) membre(s)',
        description:
          'Supprimer un membre du compte {{count}} est une action permanente. Vous aurez la possibilité de réinviter un membre à tout moment.',
        are_you_sure: 'Êtes-vous sûr de vouloir supprimer le(s) membre(s) suivant(s) ?',
        cancel: 'Annuler',
        submit: 'Oui, supprimer le(s) membre(s) du compte',
      },
      add_network_modal: {
        button: 'Ajouter un réseau',
        title: 'Ajouter un réseau',
        refresh_network: 'Actualiser le réseau',
        edit_network: 'Modifier le réseau',
        remove_network: 'Supprimer le réseau',
        remove_network_description:
          'Supprimez le réseau de votre liste enregistrée pour ce véhicule / navire. La suppression du réseau est définitive. Vous pouvez toujours rajouter le réseau.',
        network_name: 'Nom du réseau (SSID)',
        network_name_helperText: 'ID du réseau du routeur wifi',
        password: 'Mot de passe',
        password_helperText: 'Mot de passe du routeur wifi',
        error_title: "Une erreur s'est produite",
        error_description: 'Impossible de mettre à jour le réseau. Veuillez réessayer plus tard.',
        priority: 'Priorité',
      },
      wifi_polling_modal: {
        title: 'Updating Network Settings',
        title_error: 'Network Settings Update',
        connecting: 'Connecting to your device...',
        searching: 'Searching for your network settings...',
        almost: 'Almost there, fetching configuration...',
        hang_tight: 'Still working on it, hang tight...',
        communicating: 'Communicating with the gateway...',
        moment: 'Just a moment longer...',
        timeout: 'Unable to reach your device. Please try again later.',
        error: 'Something went wrong while updating network settings. Please try again.',
      },
      wifi_error_log_modal: {
        title: "Journal d'erreurs",
        no_errors: 'Aucune erreur trouvée',
        close: 'Fermer',
      },
      digital_services_modal: {
        activate_service_content: {
          title: 'Activer le service',
          description:
            "Pour nous aider à préparer le contrat d'activation approprié et à demander les bonnes coordonnées, veuillez préciser au nom de qui vous activez ce service.",
          myself: 'Moi-même',
          myself_helper: 'Activez ce service pour votre machine ou votre navire personnel.',
          company: 'Une entreprise',
          company_helper: "Activez ce service au nom d'une entreprise, d'une flotte ou d'une organisation.",
        },
        contract_details_content: {
          title: 'Détails du contrat',
          first_name: 'Prénom',
          last_name: 'Nom',
          email_address: 'Adresse e-mail',
          company_name: "Nom de l'entreprise",
          company_details_title: "Informations sur l'entreprise",
          company_details_description: "Saisissez les informations de l'entreprise propriétaire de la machine.",
          vat_number: 'Numéro de TVA',
          social_security_number: 'Numéro de sécurité sociale',
          state_province: 'État/Province',
          country: 'Pays',
          address: 'Adresse',
          city: 'Ville',
          postal_code: 'Code postal',
          is_correct_info:
            'Je confirme par la présente que les informations fournies ci-dessus sont exactes et correctes.',
          is_terms_checked:
            "Je reconnais et confirme avoir lu et compris les termes et conditions de l'Accord sur les Services Numériques (y compris les annexes) et que je suis autorisé à accepter ledit <a>contrat</a> avec effet juridiquement contraignant.",
          data_management_agreement: "J'ai également lu et compris l'<a>Accord de Gestion des Données</a>.",
          remote_software_updates_title: 'Remote Software Updates',
          service_description_for: 'Description du service pour {{serviceName}}',
          is_service_details_checked:
            'Je reconnais et confirme avoir lu et compris le <serviceDescription /> et que je suis autorisé à accepter lesdits détails du service.',
          is_data_act_checked:
            "La loi européenne sur les données exige que nous vous fournissions des informations précontractuelles décrivant comment un produit Volvo Penta connecté (mis sur le marché dans un État membre de l'UE) génère des données produit, comment les données produit sont collectées, comment vous, en tant qu'utilisateur, pouvez y accéder et comment elles sont utilisées par Volvo Penta. Les informations complètes sont disponibles sur <a>EU Data Act | Volvo Penta</a>. En cochant cette case et en poursuivant l'achat, la location ou la location du produit Volvo Penta connecté, vous reconnaissez avoir eu accès à ces informations précontractuelles conformément à la loi européenne sur les données.",
        },
        choose_receiver_content: {
          title: 'Choisir un concessionnaire',
          description:
            "Veuillez ajouter un concessionnaire pour recevoir les notifications d'alerte. Cela garantit un temps de fonctionnement maximal et une planification de service la plus efficace pour votre produit Volvo Penta.",
          find_nearest_dealer: 'Trouvez votre concessionnaire le plus proche',
          dealer: 'Concessionnaire',
          dealer_placeholder: 'Entrez le nom du concessionnaire et/ou son adresse e-mail',
        },
        thanks_content: {
          title: 'Merci !',
          description:
            "Veuillez noter que l'activation n'est pas immédiate et prendra un certain temps. Nous vous informerons par e-mail lorsque le service sera prêt à être utilisé.",
          description_all_services:
            'Nous vous informerons par e-mail une fois que les services seront prêts à être utilisés.',
          Marine: {
            description:
              "Veuillez noter que l'activation n'est pas immédiate et peut prendre un certain temps. Nous vous informerons par e-mail une fois l'activation terminée.",
          },
        },
        deactivate_service: {
          title: 'Résilier le service',
          description: 'Êtes-vous sûr de vouloir résilier le service ?',
          description_all: 'Êtes-vous sûr de vouloir résilier tous les services, ({{serviceNames}}) ?',
          deactivate: 'Résilier',
        },
      },
    },
    table: {
      items_selected: '{{selectedRowsCount}} éléments sélectionnés',
      page: 'Page {{currentPage}} sur {{totalPages}}',
      unit_table: {
        unit_name: "Nom de l'unité",
        model_brand: 'Modèle/marque',
        location: 'Emplacement',
        engines: 'Moteurs',
        operating_hours: 'Heures de fonctionnement',
        fleets: 'Flottes',
        campaigns: 'Campagnes',
        digital_services: 'Services numériques',
        unverified_ownership: 'Propriété non vérifiée',
      },
      campaigns_table: {
        campaign: 'Campagne',
        download: 'Télécharger',
        created_date: 'Date de création',
        valid_to: "Valide jusqu'au",
        product: 'Produit',
        type: 'Type',
      },
      members_table: {
        name: 'Nom',
        email: 'E-mail',
        account: 'Compte',
      },
      invitations_table: {
        creation_date: 'Date de création',
      },
      specifikationer_table: {
        driveline: 'Chaîne cinématique',
        product_type: 'Type de produit',
        product: 'Produit',
        serial_number: 'Numéro de série',
        operating_hours: 'Heures de fonctionnement',
        engine: 'Moteur',
        segment: 'Segment',
        rated_power: 'Puissance nominale',
        rated_speed: 'Vitesse nominale',
      },
      network_settings_table: {
        priority: 'Priorité',
        ssid: 'SSID',
        frequency: 'Fréquence',
        last_updated: 'Dernière mise à jour',
        connection: 'Connexion',
        connected: 'Connecté',
        not_connected: 'Non connecté',
        lost_connection: 'Connexion perdue',
        disconnected: 'Déconnecté',
      },
      coverage_table: {
        type: 'Type',
        description: 'Description de la garantie/couverture',
        expiration_hours: "Heures d'expiration",
        expiration_date: "Date d'expiration",
      },
      fault_codes_table: {
        unit: 'Unité',
        severity: 'Gravité',
        fault_code: 'Code défaut',
        fault_description: 'Description du défaut',
        failure_event: 'Événement de défaillance',
        timestamp: 'Horodatage',
        operating_hours: 'Heures de fonctionnement',
        occurrences: 'Occurrences',
      },
      connectivity_table: {
        unit: 'Unité',
        operating_hours: 'Heures de fonctionnement',
        last_seen: 'Dernière vue',
        status: 'Statut de connectivité',
      },
      fuel_consumption_table: {
        unit: 'Unité',
        chassis_id: 'ID du châssis',
        operating_hours: 'Heures de fonctionnement (h)',
        fuel_efficiency: 'Efficacité énergétique (l/h)',
        total_fuel: 'Carburant total (l)',
      },
      publications_table: {
        title: 'Titre',
        description: 'Description',
        designation: 'Produit',
        product: 'Type de produit',
        size: 'Taille',
        languageCode: 'Lang.',
        buy: 'Acheter',
      },
      maintenance_dealer_table: {
        header: 'Concessionnaire',
        dealer_name: 'Nom du concessionnaire',
        location: 'Emplacement',
      },
      maintenance_protocol_details_table: {
        header: 'Détails du protocole de service',
        title: 'Titre',
        current_service: 'Service actuel',
        previous_service: 'Service précédent',
        date: 'Date',
        operating_hours: 'Heures de fonctionnement',
        unit_age: "Âge de l'unité",
        months: 'mois',
      },
      maintenance_driveline_table: {
        header: 'Chaîne cinématique {{driveline}}',
        name: 'Nom',
        serial_number: 'Numéro de série',
        product: 'Produit',
      },
      maintenance_service_table: {
        header: 'Service {{service}}',
        activity: 'Activité',
        detail: 'Détail',
        last_performed: 'Dernière exécution',
        performed: 'Exécuté',
        complete: 'Terminé',
        not_performed: 'Non exécuté',
      },
    },
    tooltip: {
      copy_clipboard: 'Copier dans le presse-papiers',
    },
    language_selector: {
      de: 'Sprache - Deutsch',
      en: 'Language - English',
      fr: 'Langue - Français',
      it: 'Lingua - Italiano',
      nl: 'Taal - Nederlands',
      pt: 'Idioma - Português',
      sv: 'Språk - Svenska',
    },
    main_menu: {
      home: 'Accueil',
      settings: 'Paramètres',
      support: 'Support',
    },
    user_menu: {
      title: 'Menu utilisateur',
      account_settings: 'Paramètres du compte',
      logout: 'Se déconnecter',
      switch_fleet_account: 'Changer de compte flotte',
    },
    footer: {
      useful: 'Utile',
      manuals_and_handbooks: 'Manuels et guides',
      dealer_locator: 'Localisateur de concessionnaires',
      warranty: 'Garantie',
      parts_column: 'Pièces et accessoires',
      parts: 'Pièces',
      remanufactured: 'Composants et moteurs reconditionnés',
      contact: 'Contact',
      action_service: 'Volvo Penta Action Service',
      faq: 'FAQ',
      contact_us: 'Nous contacter',
      privacy_policy: 'Politique de confidentialité',
      cookie_policy: 'Politique relative aux cookies',
    },
    cta: {
      members_fleetaccount_cta: {
        heading: "Inviter d'autres personnes à votre compte",
        message:
          "Ajoutez des membres pour partager l'accès à vos unités et flottes. Vous pouvez inviter des collègues, des partenaires commerciaux ou des membres de votre famille et attribuer des rôles pour contrôler ce qu'ils peuvent voir et gérer. Copiez le lien et envoyez-le à votre membre.",
      },
      members_digitalaccount_cta: {
        heading: "Inviter d'autres personnes à votre compte",
        message:
          "L'invitation de membres nécessite un propriétaire d'unité vérifié. Une fois votre propriété confirmée, vous pourrez inviter des collègues, des partenaires commerciaux ou des membres de votre famille et attribuer des rôles pour contrôler ce qu'ils peuvent voir et gérer.<br /><br />Pour vérifier votre propriété, veuillez contacter votre concessionnaire Volvo Penta agréé.",
      },
      action_cta: {
        heading: 'Comment agir',
        message: 'Pour effectuer une campagne, contactez votre concessionnaire Volvo Penta ou OEM.',
        find_dealer: 'Trouvez votre concessionnaire le plus proche',
        learn_more: 'En savoir plus',
      },
      notifications_cta: {
        heading: 'Notifications',
        message:
          "Restez informé et recevez des notifications par e-mail lorsqu'une unité dispose d'une campagne installable disponible.",
        notification_settings: 'Paramètres de notification',
      },
    },
    role_types: {
      title: 'Informations sur les rôles',
      manager: {
        title: 'Manager',
        description:
          "Les managers ont un contrôle administratif complet pour superviser la flotte, surveiller l'état des unités et gérer toutes les unités, les paramètres et les accès utilisateurs.",
      },
      technician: {
        title: 'Technician',
        description:
          "Se concentre sur l'état et la maintenance des unités. Les techniciens peuvent surveiller les données des unités et effectuer des mises à jour logicielles à distance. Ce rôle n'inclut pas les permissions de gestion de flotte ou d'utilisateurs.",
      },
      viewer: {
        title: 'Viewer',
        description:
          "Les observateurs ont un accès en lecture seule. Ils peuvent surveiller l'état des unités et consulter les rapports, mais ne peuvent pas modifier les paramètres ou les configurations.",
      },
    },
    campaign_types: {
      title: 'Comprendre les types de campagnes',
      recall: {
        title: 'Rappel',
        description:
          "Introduction rétroactive d'une modification de production ; doit être mise en œuvre dès que possible afin de garantir le bon fonctionnement du produit.",
      },
      quality: {
        title: 'Qualité',
        description:
          "Introduction rétroactive d'une modification de production ; doit être mise en œuvre lors du prochain entretien planifié afin de garantir le bon fonctionnement du produit.",
      },
      service: {
        title: 'Service',
        description:
          "Introduction rétroactive d'une modification de production liée à la sécurité ; doit être mise en œuvre immédiatement afin de garantir un fonctionnement sûr du produit.",
      },
      installation: {
        title: 'Installation',
        description:
          "Une mise à jour logicielle à distance est prête à être installée sur votre unité à l'aide de l'application Volvo Penta.",
        app_store: "Disponible pour iOS sur l'App Store",
        google_play: 'Disponible pour Android sur Google Play',
      },
    },
  },
};

export default frTranslation;
